/* eslint-disable no-nested-ternary */
/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-unstable-nested-components */
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import type { SeriesLineOptions, SeriesColumnOptions } from "highcharts";
import sum from "lodash/sum";
import uniqBy from "lodash/uniqBy";
import { BarChartIcon } from "lucide-react";
import type { SetStateAction } from "react";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LineBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import FullBleedButton from "components/financials/modern/FullBleedButton";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import { useFinancialsChart } from "components/financials/modern/data/hooks";
import type { IFinancialsSheetQuery } from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import IndicatorsButtonArray from "components/financials/modern/indicators/IndicatorsButtonArray";
import SelectArbitraryIndicatorDebouncer from "components/financials/modern/indicators/SelectArbitraryIndicatorDebouncer";
import SelectArbitraryIndicatorPopover from "components/financials/modern/indicators/SelectArbitraryIndicatorPopover";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import getPlatformVerticalByIssuer from "components/financials/modern/utils/platformVertical";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import aeonik from "components/utils/aeonik";

import useSelfie from "lib/hooks/useSelfie";

import type { IIndicatorDataSource, IPlatformVertical, ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType } from "types/graphqlTypes";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import UnlockPremiumContentCTA, { TrialCTAContentForChart } from "components/conversion/UnlockPremiumContentCTA";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import parse from "date-fns/parse";

interface FinancialsChartProps {
  vertical: IPlatformVertical;
  tickerSymbol: string;
  country: ISecurityCountry;
  indicatorDataSource: IIndicatorDataSource;
  currency?: string;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  arbitraryIndicators: IIndicatorFieldsFragment[];
  selectedIndicators: IIndicatorFieldsFragment[];
  templateId?: string | null;
  onChangeSelectedIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeArbitraryIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeStatementType?: (statementType: IFinancialsStatementType) => void;
  onChangeInterim?: (interim: string | null) => void;
}

export default function FinancialsChart({
  vertical,
  tickerSymbol,
  country,
  indicatorDataSource,
  statementType,
  interim,
  currency = "USD",
  arbitraryIndicators,
  selectedIndicators,
  templateId,
  onChangeSelectedIndicators,
  onChangeArbitraryIndicators,
  onChangeStatementType,
  onChangeInterim,
}: FinancialsChartProps) {
  const issuer = useRootIssuer();
  const platformVertical = getPlatformVerticalByIssuer(issuer, indicatorDataSource);
  const { loading, financialsSheet, financialsTemplate } = useFinancialsChart({
    tickerSymbol,
    country,
    statementType: statementType || IFinancialsStatementType.Annual,
    templateId: templateId || null,
    currency,
    arbitraryIndicators: selectedIndicators,
    interim: interim || null,
    vertical: platformVertical,
    indicatorDataSource,
  });

  return (
    <Chart
      vertical={vertical}
      loading={loading}
      financialsSheet={financialsSheet}
      financialsTemplate={financialsTemplate}
      indicatorDataSource={indicatorDataSource}
      selectedIndicators={selectedIndicators}
      arbitraryIndicators={arbitraryIndicators}
      onSetSelectedIndicators={onChangeSelectedIndicators}
      interim={interim}
      statementType={statementType}
      onChangeStatementType={onChangeStatementType}
      onChangeArbitraryIndicators={onChangeArbitraryIndicators}
      onChangeInterim={onChangeInterim}
    />
  );
}

interface ChartProps {
  vertical: IPlatformVertical;
  loading: boolean;
  financialsSheet: IFinancialsSheetQuery["financialsSheet"] | null;
  financialsTemplate: IFinancialsSheetQuery["financialsSheet"]["systemTemplate"] | null;
  selectedIndicators: IIndicatorFieldsFragment[];
  arbitraryIndicators: IIndicatorFieldsFragment[];
  indicatorDataSource: IIndicatorDataSource;
  statementType?: IFinancialsStatementType;
  interim?: string | null;
  onSetSelectedIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeArbitraryIndicators: (setState: SetStateAction<IIndicatorFieldsFragment[]>) => void;
  onChangeStatementType?: (statementType: IFinancialsStatementType) => void;
  onChangeInterim?: (interim: string | null) => void;
}

function Chart({
  vertical,
  loading,
  financialsSheet,
  financialsTemplate,
  indicatorDataSource,
  statementType,
  interim,
  selectedIndicators,
  arbitraryIndicators,
  onSetSelectedIndicators,
  onChangeArbitraryIndicators,
  onChangeStatementType,
  onChangeInterim,
}: ChartProps) {
  const intl = useIntl();
  const viewer = useSelfie();
  const theme = useTheme();
  const [plotType, setPlotType] = useState<"line" | "column">("line");

  return (
    <div className="flex h-full w-full items-start space-x-6">
      <div className="flex h-full w-full flex-col space-y-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[_1fr,_1fr]">
          <div className="flex flex-wrap items-center gap-4 sm:flex-nowrap">
            <SelectArbitraryIndicatorDebouncer
              selectedIndicators={selectedIndicators}
              onToggleIndicator={(indicator, isSelected) => {
                const nextSelectedIndicators = isSelected
                  ? selectedIndicators.filter(({ objectId }) => objectId !== indicator.objectId)
                  : [...selectedIndicators, indicator];

                onChangeArbitraryIndicators(nextSelectedIndicators);
              }}
            >
              {({ selectedIndicators, onToggleIndicator }) => (
                <SelectArbitraryIndicatorPopover
                  vertical={vertical}
                  indicatorDataSource={indicatorDataSource}
                  mode="TOGGLE"
                  // limit={2}
                  selectedIndicators={selectedIndicators}
                  onToggleIndicator={onToggleIndicator}
                />
              )}
            </SelectArbitraryIndicatorDebouncer>

            <div className="flex w-full items-center gap-4 sm:w-auto">
              {statementType !== IFinancialsStatementType.Monthly && (
                <SelectStatementType
                  disabled={loading || !viewer?.isPremium}
                  interim={interim}
                  statementType={statementType}
                  onChangeInterim={onChangeInterim}
                  onChangeStatementType={onChangeStatementType}
                />
              )}

              <LineBarPlotTypeSelector value={plotType} onChange={setPlotType} />
            </div>
          </div>

          {viewer && viewer.isPremium && (
            <div className="hidden justify-end gap-4 lg:flex">
              <FullBleedButton />
            </div>
          )}
        </div>

        {((financialsTemplate && financialsTemplate.indicators.length > 0) || arbitraryIndicators.length > 0) && (
          <IndicatorsButtonArray
            selectedIndicators={selectedIndicators}
            indicators={uniqBy([...arbitraryIndicators, ...(financialsTemplate?.indicators || [])], "objectId")}
            onToggleIndicator={(indicator, isSelected) => {
              const nextSelectedIndicators = isSelected
                ? selectedIndicators.filter(({ objectId }) => objectId !== indicator.objectId)
                : [...selectedIndicators.slice(-4), indicator];

              onSetSelectedIndicators?.(nextSelectedIndicators);
            }}
          />
        )}

        {(!financialsSheet || financialsSheet?.indicators.length === 0) && (
          <div
            className={classNames(
              "rounded-xl bg-amber-500/5 p-4 text-center text-sm tracking-wide text-amber-600",
              "space-y-4",
            )}
          >
            <BarChartIcon className="inline-flex h-6 w-6" />
            <p className="text-center font-medium">
              <FormattedMessage
                defaultMessage="Selecione ao menos um indicador para desenhar um gráfico."
                id="JdGr9d"
              />
            </p>
          </div>
        )}

        {financialsSheet && financialsSheet.indicators.length > 0 && (
          <>
            <HighchartsChart
              immutable={true}
              options={getChartOptions(intl, plotType, "linear", theme as "dark" | "light", financialsSheet)}
              className="h-96 xl:h-full"
            />

            {(!viewer?.isPremium || viewer.isTrialing) && (
              <UnlockPremiumContentCTA
                origin="Financials Chart"
                freeMessage={intl.formatMessage({
                  defaultMessage: "Para ver o gráfico completo, você precisa ser Premium.",
                  id: "TR/suc",
                })}
                trialMessage={<TrialCTAContentForChart />}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function getChartOptions(
  intl: IntlShape,
  plotType: "column" | "line",
  axisScaleType: AxisScaleType,
  theme: "light" | "dark",
  financialsSheet: IFinancialsSheetQuery["financialsSheet"],
) {
  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const isMulti = financialsSheet.indicators.length > 1;
  const isNegative = financialsSheet.indicators.every((indicator) => {
    return indicator.series.every((node) => {
      return isValueNode(node) ? node.value <= 0 : true;
    });
  });
  // const columnThemes = financialsSheet.indicators.map((indicator) => indicator.columnTheme);
  // const backgroundColors = defaultColorScheme.redirectColorsAsHex(columnThemes.map((theme) => theme.backgroundColor));
  // const textColors = defaultColorScheme.redirectColorsAsHex(columnThemes.map((theme) => theme.textColor));
  // const betterColumnThemes = columnThemes.map((theme, index) => {
  //   return {
  //     ...theme,
  //     backgroundColor: backgroundColors[index],
  //     textColor: textColors[index],
  //   };
  // });

  const series: (SeriesLineOptions | SeriesColumnOptions)[] = financialsSheet.indicators.map(
    (indicator, indicatorIndex) => {
      const theme = getColorThemeForIndex(indicatorIndex);

      if (!theme) {
        throw new Error("Theme not found.");
      }
      const { color: backgroundColor, lineColor } = theme;
      const documentation = getIndicatorDocumentation(intl, indicator);

      const defaultOptions: SeriesLineOptions | SeriesColumnOptions = {
        showInNavigator: indicatorIndex === 0,
        type: plotType,
        animation: false,
        name: documentation.name,
        yAxis: indicatorIndex,
        color: lineColor,
        lineWidth: 2.5,
        data: financialsSheet.reportDates
          .map((date, dataIndex) => {
            const node = financialsSheet.indicators[indicatorIndex].series[dataIndex];

            if (!isValueNode(node)) {
              return null;
            }

            const absoluteSeriesValues = financialsSheet.indicators[indicatorIndex].series.map((resolvedValue) => {
              return isValueNode(resolvedValue) ? Math.abs(resolvedValue.value) : 0;
            });

            const resolvedValue = isValueNode(node) ? node.value : 0;
            const totalSum = sum(absoluteSeriesValues);
            const value = isMulti
              ? (Math.abs(resolvedValue) / totalSum) * 100
              : isNegative
                ? Math.abs(resolvedValue)
                : resolvedValue;

            return {
              x: parseISO(date).getTime(),
              y: value,
              absoluteY: resolvedValue,
              custom: {
                indicatorName: documentation.abbreviation,
                asOfDate: date,
                value,
                formattedValue: financialsSheet.indicators[indicatorIndex].series[dataIndex].formattedValue,
              },
            };
          })
          .filter(notUndefinedOrNull)
          .sort((a, b) => compareAsc(a.x, b.x)),
        custom: {
          backgroundColor,
          textColor: lineColor,
        },
        marker: {
          width: 8,
          height: 8,
          lineWidth: 1,
          fillColor: lineColor,
          color: backgroundColor,
          lineColor: lineColor,
          symbol: "circle",
        },
      };

      if (plotType === "column") {
        const borderColor = lineColor;
        const columnOptions = {
          borderColor,
          borderWidth: 1.25,
          states: {
            hover: {
              color: borderColor,
              borderColor: borderColor,
              backgroundColor: backgroundColor,
            },
          },
        };
        return {
          ...columnOptions,
          ...defaultOptions,
        };
      }

      return defaultOptions;
    },
  );

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        type: "x",
        enabled: true,
      },
      panKey: "shift",
      alignTicks: true,
      animation: false,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineDashStyle: "Solid",
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 0,
      lineColor: slateOrDarkSlate,
      tickWidth: 1,
      tickColor: slateOrDarkSlate,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: labelsColor,
          fontWeight: "500",
          fontSize: "12px",
          fontFamily: aeonik.style.fontFamily,
        },
        formatter: function () {
          return intl.formatDate(this.value, {
            year: "2-digit",
            month: "numeric",
          });
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: theme === "light" ? colors.slate[800] : colors.slate[500],
      },
    },
    yAxis: financialsSheet.indicators.map((indicator, index) => {
      const values = indicator.series.map((node) => {
        return isValueNode(node) ? node.value : 0;
      });

      // const minValue = values.length > 0 ? Math.min(...values) : 0;
      const allValuesBelowZero = values.every((value) => value <= 0);
      const hasNegativeValue = values.some((value) => value < 0);
      // const valuesBelowZero = [...values.sort((a, b) => b - a)].filter((value) => value < 0);
      // const firstValueBelowZero = valuesBelowZero[0];

      return {
        type: axisScaleType,
        // min: isMulti || isNegative ? 0 : minValue,
        plotLines:
          financialsSheet.indicators.length === 1 && !allValuesBelowZero && hasNegativeValue
            ? [
                {
                  color: theme === "dark" ? colors.slate[600] : colors.slate[950],
                  // value: firstValueBelowZero,
                  value: 0,
                  dashStyle: "Dash",
                  width: 1.5,
                },
              ]
            : undefined,
        gridLineWidth: 0,
        gridLineColor: slateOrDarkSlate,
        lineWidth: 1,
        lineColor: slateOrDarkSlate,
        crosshair: {
          snap: true,
          dashStyle: "Dash",
          color: theme === "light" ? colors.slate[800] : colors.slate[500],
        },
        labels: {
          enabled: !isMulti,
          align: "center",
          style: {
            color: labelsColor,
            fontFamily: aeonik.style.fontFamily,
            fontWeight: "500",
            fontSize: "12px",
          },
          formatter: function (this) {
            const actualValue = this.value;
            const isPercent = indicator.series.find(
              (node) => node.__typename === "FinancialIndicatorSeriesPercentNode",
            );

            if (isPercent || isMulti) {
              const percentageFormatter = new Intl.NumberFormat("en-US", {
                style: "percent",
                maximumFractionDigits: 2,
              });

              return percentageFormatter.format(parseFloat(String(actualValue)));
            }

            const valueFormatter = new Intl.NumberFormat("en", {
              notation: "compact",
              minimumFractionDigits: 0,
              maximumFractionDigits: 4,
            });
            return valueFormatter.format(parseFloat(String(actualValue)));
          },
        },
        tickWidth: 0,
        showFirstLabel: true,
        showLastLabel: false,
        scrollbar: {
          enabled: false,
        },
        opposite: index > 0,
      };
    }),
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...(theme === "dark" ? darkNavigatorStyles : navigatorStyles),
      yAxis: {
        ...navigatorStyles.yAxis,
        type: axisScaleType,
      },
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: false,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: theme === "dark" ? colors.slate[800] : colors.white,
      borderColor: theme === "dark" ? colors.slate[950] : colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        if (!this.points || this.points.length === 0) {
          return "";
        }

        const point = (this.point as any).custom as Record<string, any> | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        return renderToStaticMarkup(
          <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
            <span
              className={classNames(
                "inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600",
                "dark:bg-slate-950 dark:text-orange-300",
              )}
            >
              {intl.formatDate(parse(point.asOfDate, "yyyy-MM-dd", new Date()), {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              })}
            </span>

            {this.points
              .sort((a, b) => {
                return (b.y || 0) - (a.y || 0);
              })
              .map(({ key, series, point }) => {
                const node = (point as any).custom as Record<string, any> | null | undefined;
                if (!key || !node) {
                  return "";
                }
                const backgroundColor = String(series.options.custom?.backgroundColor);
                const textColor = String(series.options.custom?.textColor);
                return (
                  <div key={key} className="flex items-center justify-between space-x-3">
                    <span
                      className="rounded-md px-2 py-1 font-mono text-xs font-bold"
                      style={{
                        backgroundColor,
                        color: textColor,
                      }}
                    >
                      {node.indicatorName}
                    </span>

                    <span className="font-mono font-semibold dark:text-slate-300">{node.formattedValue}</span>
                  </div>
                );
              })}
          </div>,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: theme === "dark" ? colors.slate[400] : colors.slate[500],
            color: theme === "dark" ? colors.slate[400] : colors.slate[50],
            lineWidth: 2,
          },
        },
      },
      line: {
        stacking: "normal",
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
      },
      column: {
        dataGrouping: {
          enabled: true,
          approximation: "average",
        },
        groupPadding: series.length ? 0.15 : 0,
        borderRadius: {
          where: "end",
          radius: 8,
          scope: "stack",
        },
      },
    },
  };

  return options;
}

function isValueNode<BracketNode extends IFinancialsSheetQuery["financialsSheet"]["indicators"][0]["series"][0]>(
  node: BracketNode,
): node is Exclude<
  BracketNode,
  { __typename: "FinancialIndicatorSeriesTextNode" | "FinancialIndicatorSeriesSkeletonNode" }
> {
  return (
    node.__typename !== "FinancialIndicatorSeriesTextNode" && node.__typename !== "FinancialIndicatorSeriesSkeletonNode"
  );
}
